import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { hasRole } from '../utils/roleHelpers';
import { toast } from 'react-toastify';
import api, { getApiUrl } from '../services/api';
import ConfirmDialog from '../components/ConfirmDialog';
import './Profile.css';

const Profile = () => {
  const { user, fetchUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [userData, setUserData] = useState({
    full_name: '',
    phone: '',
    email: '',
    username: '',
  });
  
  // Estados para cambio de contraseña
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('profile'); // 'profile', 'password' o 'organization'
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [confirmLogoDelete, setConfirmLogoDelete] = useState(false);
  const fileInputRef = useRef(null);

  const isOwner = user && !user.is_superuser && hasRole(user, 'owner');

  useEffect(() => {
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      setLoadingProfile(true);
      const response = await api.get('/auth/me');
      const userInfo = response.data;
      setUserData({
        full_name: userInfo.full_name || '',
        phone: userInfo.phone || '',
        email: userInfo.email || '',
        username: userInfo.username || '',
      });
    } catch (error) {
      console.error('Error al cargar perfil:', error);
      toast.error('Error al cargar la información del perfil');
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setUserData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.put('/auth/me', {
        full_name: userData.full_name,
        phone: userData.phone,
      });
      
      toast.success('Perfil actualizado correctamente');
      // Actualizar el contexto de autenticación
      if (fetchUser) {
        await fetchUser();
      }
    } catch (error) {
      console.error('Error al actualizar perfil:', error);
      const errorMessage = error.response?.data?.detail || 'Error al actualizar el perfil';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const currentPassword = form.elements.current_password.value.trim();
    const newPassword = form.elements.new_password.value.trim();
    const confirmPassword = form.elements.confirm_password.value.trim();

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Todos los campos son requeridos');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (currentPassword === newPassword) {
      toast.error('La nueva contraseña debe ser diferente a la actual');
      return;
    }

    setLoading(true);

    try {
      await api.put('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      
      toast.success('Contraseña actualizada correctamente');
      // Limpiar formulario
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
    } catch (error) {
      console.error('Error al cambiar contraseña:', error);
      const errorMessage = error.response?.data?.detail || 'Error al cambiar la contraseña';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tamaño (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('El archivo no debe superar los 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await api.post(`/tenants/${user.tenant_id}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Logo actualizado correctamente');
      // Refrescar datos del usuario para obtener la nueva URL del logo
      if (fetchUser) await fetchUser();
    } catch (error) {
      console.error('Error al subir logo:', error);
      toast.error(error.response?.data?.detail || 'Error al subir el logo');
    } finally {
      setUploadingLogo(false);
      // Limpiar el input para permitir subir el mismo archivo de nuevo
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleLogoDelete = async () => {
    setUploadingLogo(true);
    try {
      await api.delete(`/tenants/${user.tenant_id}/logo`);
      toast.success('Logo eliminado correctamente');
      if (fetchUser) await fetchUser();
    } catch (error) {
      console.error('Error al eliminar logo:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar el logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  if (loadingProfile) {
    return (
      <div className="page-container">
        <div className="card">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>Cargando información del perfil...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <ConfirmDialog
        isOpen={confirmLogoDelete}
        onClose={() => setConfirmLogoDelete(false)}
        onConfirm={handleLogoDelete}
        title="¿Eliminar el logo?"
        message="La organización quedará sin logo hasta que subas uno nuevo."
        type="danger"
        confirmText="Eliminar"
      />
      <div className="page-header">
        <h1>👤 Mi Perfil</h1>
      </div>

      <div className="profile-container">
        {/* Tabs */}
        <div className="profile-tabs">
          <button
            className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            📝 Información Personal
          </button>
          <button
            className={`tab-button ${activeTab === 'password' ? 'active' : ''}`}
            onClick={() => setActiveTab('password')}
          >
            🔒 Cambiar Contraseña
          </button>
          {isOwner && (
            <button
              className={`tab-button ${activeTab === 'organization' ? 'active' : ''}`}
              onClick={() => setActiveTab('organization')}
            >
              🏢 Mi Organización
            </button>
          )}
        </div>

        {/* Tab: Información Personal */}
        {activeTab === 'profile' && (
          <div className="card">
            <h2>Información Personal</h2>
            <form onSubmit={handleProfileSubmit} className="profile-form">
              <div className="form-group">
                <label>Usuario</label>
                <input
                  type="text"
                  value={userData.username}
                  disabled
                  className="disabled-input"
                />
                <small>El nombre de usuario no se puede cambiar</small>
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={userData.email}
                  disabled
                  className="disabled-input"
                />
                <small>El email no se puede cambiar desde aquí. Contacta al administrador.</small>
              </div>

              <div className="form-group">
                <label>Nombre Completo</label>
                <input
                  type="text"
                  name="full_name"
                  value={userData.full_name}
                  onChange={handleProfileChange}
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Teléfono</label>
                <input
                  type="tel"
                  name="phone"
                  value={userData.phone}
                  onChange={handleProfileChange}
                  placeholder="+51 900 000 000"
                  disabled={loading}
                />
              </div>

              {user?.tenant && (
                <div className="form-group">
                  <label>Organización</label>
                  <input
                    type="text"
                    value={`${user.tenant.name} (${user.tenant.code})`}
                    disabled
                    className="disabled-input"
                  />
                </div>
              )}

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Guardando...' : 'Guardar Cambios'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Tab: Cambiar Contraseña */}
        {activeTab === 'password' && (
          <div className="card">
            <h2>Cambiar Contraseña</h2>
            <form onSubmit={handlePasswordSubmit} className="profile-form">
              <div className="form-group">
                <label>Contraseña Actual</label>
                <div className="password-input-wrapper">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    name="current_password"
                    autoComplete="current-password"
                    value={passwordData.current_password}
                    onChange={handlePasswordChange}
                    required
                    placeholder="Ingrese su contraseña actual"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    aria-label={showCurrentPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    disabled={loading}
                  >
                    {showCurrentPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Nueva Contraseña</label>
                <div className="password-input-wrapper">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    name="new_password"
                    autoComplete="new-password"
                    value={passwordData.new_password}
                    onChange={handlePasswordChange}
                    required
                    placeholder="Mínimo 6 caracteres"
                    minLength={6}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    aria-label={showNewPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    disabled={loading}
                  >
                    {showNewPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
                <small>La contraseña debe tener al menos 6 caracteres</small>
              </div>

              <div className="form-group">
                <label>Confirmar Nueva Contraseña</label>
                <div className="password-input-wrapper">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirm_password"
                    autoComplete="new-password"
                    value={passwordData.confirm_password}
                    onChange={handlePasswordChange}
                    required
                    placeholder="Confirme su nueva contraseña"
                    minLength={6}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    disabled={loading}
                  >
                    {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Cambiando...' : 'Cambiar Contraseña'}
                </button>
              </div>
            </form>
          </div>
        )}
        {/* Tab: Mi Organización (solo owners) */}
        {activeTab === 'organization' && isOwner && (
          <div className="card">
            <h2>Logo de la Organización</h2>
            <div className="org-logo-section">
              <div className="org-logo-preview">
                {user?.tenant?.logo_url ? (
                  <img
                    src={`${getApiUrl()}/tenants/${user.tenant.id}/logo?v=${encodeURIComponent(user.tenant.updated_at || new Date().toISOString())}`}
                    alt="Logo de la organización"
                    className="org-logo-img"
                  />
                ) : (
                  <div className="org-logo-placeholder">
                    <span>🏢</span>
                    <p>Sin logo</p>
                  </div>
                )}
              </div>
              <div className="org-logo-actions">
                <p className="org-logo-hint">
                  Sube el logo de tu club o academia. Se mostrará en la esquina superior derecha del dashboard.
                </p>
                <p className="org-logo-hint">
                  Formatos: PNG, JPG, WEBP, SVG. Máximo 2MB.
                </p>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  style={{ display: 'none' }}
                  onChange={handleLogoUpload}
                />
                <div className="org-logo-buttons">
                  <button
                    className="btn btn-primary"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingLogo}
                  >
                    {uploadingLogo ? 'Subiendo...' : (user?.tenant?.logo_url ? 'Cambiar Logo' : 'Subir Logo')}
                  </button>
                  {user?.tenant?.logo_url && (
                    <button
                      className="btn btn-danger"
                      onClick={() => setConfirmLogoDelete(true)}
                      disabled={uploadingLogo}
                    >
                      Eliminar Logo
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;

